import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { MainErrorFallback } from '@/components/errors/main';
import { env } from '@/config/env';
import { instrumentation } from '@/instrument';
import { queryClient } from '@/lib/react-query';
import { Notifications } from '@/ui/notifications';
import { Spinner } from '@/ui/spinner';

type AppProviderProps = {
  children: React.ReactNode;
};

export const AppProvider = ({ children }: AppProviderProps) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    instrumentation.captureException(error, {
      componentStack: errorInfo.componentStack ?? undefined,
    });
  };

  return (
    <React.Suspense
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <Spinner fullPage />
        </div>
      }
    >
      <ErrorBoundary
        FallbackComponent={MainErrorFallback}
        onError={handleError}
      >
        <QueryClientProvider client={queryClient}>
          <GoogleReCaptchaProvider
            reCaptchaKey={env.GOOGLE_RECAPTCHA_KEY ?? ''}
            useEnterprise
          >
            {env.NODE_ENV === 'development' ? <ReactQueryDevtools /> : null}
            <Notifications />
            {children}
          </GoogleReCaptchaProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </React.Suspense>
  );
};
